import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DemoVault from './DemoVault';

export default function Landing() {
  const [scrolled, setScrolled] = useState(false);
  const observerRef = useRef(null);
  const navigate = useNavigate();
  const goToAuth = () => navigate('/auth');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('revealed'); }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.reveal').forEach(el => observerRef.current.observe(el));
    return () => observerRef.current?.disconnect();
  }, []);

  const style = `
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,300;0,400;0,700;0,900;1,300;1,400;1,700;1,900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    :root {
      --cream:   #FAF7F2;
      --cream-d: #F0EBE1;
      --plum:    #1E1040;
      --bg:      #0B1220;
      --card:    #111827;
      --card-h:  #1F2937;
      --navy:    #0F172A;
      --green:   #22C55E;
      --mint:    #34D399;
      --purple:  #8B5CF6;
      --purple-l:#A78BFA;
      --blue:    #38BDF8;
      --orange:  #FB923C;
      --pink:    #EC4899;
      --grad-pb: linear-gradient(135deg, #8B5CF6, #38BDF8);
      --grad-pg: linear-gradient(135deg, #8B5CF6, #22C55E);
      --grad-p:  linear-gradient(135deg, #8B5CF6, #A78BFA);
    }

    html { scroll-behavior: smooth; }
    body { font-family: 'Plus Jakarta Sans', sans-serif; background: var(--cream); color: var(--plum); overflow-x: hidden; }

    /* NAV */
    .lg-nav {
      position: fixed; top: 0; left: 0; right: 0; z-index: 100;
      display: flex; align-items: center; justify-content: space-between;
      padding: 20px 48px;
      transition: background 0.4s, backdrop-filter 0.4s, box-shadow 0.4s;
    }
    .lg-nav.dark {
      background: rgba(250,247,242,0.95);
      backdrop-filter: blur(12px);
      box-shadow: 0 1px 0 rgba(30,16,64,0.08);
    }
    .lg-nav-logo {
      font-family: 'Fraunces', serif; font-weight: 900; font-size: 22px;
      color: var(--plum); text-decoration: none; border: none; background: none; cursor: pointer;
      transition: color 0.3s;
    }
    .lg-nav.dark .lg-nav-logo { color: var(--plum); }
    .lg-nav-logo span { color: var(--purple); font-style: italic; }
    .lg-nav-cta {
      background: var(--grad-pb); color: #fff; border: none; border-radius: 100px;
      padding: 10px 24px; font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 600; font-size: 14px; cursor: pointer;
      transition: opacity 0.2s, transform 0.2s;
    }
    .lg-nav-cta:hover { opacity: 0.88; transform: translateY(-1px); }

    /* HERO */
    .lg-hero {
      min-height: 100vh; background: var(--cream);
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      padding: 120px 48px 80px; text-align: center; position: relative; overflow: hidden;
    }
    .lg-hero-bg {
      position: absolute; inset: 0; pointer-events: none;
      background: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(139,92,246,0.08) 0%, transparent 70%);
    }
    .lg-hero-eyebrow {
      display: inline-flex; align-items: center; gap: 8px;
      background: rgba(139,92,246,0.1); border: 1px solid rgba(139,92,246,0.2);
      border-radius: 100px; padding: 6px 16px; margin-bottom: 32px;
      font-size: 13px; font-weight: 600; color: var(--purple); letter-spacing: 0.04em;
    }
    .lg-hero-eyebrow::before { content: '🗝️'; font-size: 14px; }
    .lg-hero-h1 {
      font-family: 'Fraunces', serif; font-weight: 900; font-size: clamp(48px, 7vw, 88px);
      line-height: 1.05; color: var(--plum); max-width: 900px; margin: 0 auto 12px;
    }
    .lg-hero-h1 em { font-style: italic; color: var(--purple); }
    .lg-hero-sub {
      font-size: clamp(17px, 2vw, 21px); color: #4B5563; font-weight: 400;
      max-width: 580px; margin: 0 auto 48px; line-height: 1.6;
    }
    .lg-hero-btns { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; margin-bottom: 80px; }
    .lg-btn-primary {
      background: var(--grad-pb); color: #fff; border: none; border-radius: 100px;
      padding: 16px 36px; font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 700; font-size: 16px; cursor: pointer;
      transition: opacity 0.2s, transform 0.2s;
      box-shadow: 0 8px 32px rgba(139,92,246,0.35);
    }
    .lg-btn-primary:hover { opacity: 0.88; transform: translateY(-2px); }
    .lg-btn-secondary {
      background: transparent; color: var(--plum); border: 2px solid rgba(30,16,64,0.2);
      border-radius: 100px; padding: 14px 32px;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 600; font-size: 16px; cursor: pointer;
      transition: border-color 0.2s, transform 0.2s;
    }
    .lg-btn-secondary:hover { border-color: var(--purple); transform: translateY(-2px); }

    /* VAULT ILLUSTRATION */
    .lg-vault-wrap {
      width: 100%; max-width: 700px; margin: 0 auto;
      position: relative;
    }
    .lg-vault-scene {
      background: linear-gradient(180deg, #2D3748 0%, #243044 100%);
      border-radius: 32px; padding: 48px 32px 40px;
      box-shadow: 0 40px 120px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.1);
      position: relative; overflow: hidden;
    }
    .lg-vault-scene::before {
      content: ''; position: absolute; inset: 0;
      background: radial-gradient(ellipse 60% 40% at 50% 0%, rgba(139,92,246,0.12) 0%, transparent 70%);
      pointer-events: none;
    }
    .lg-vault-title {
      font-family: 'Plus Jakarta Sans', sans-serif; font-size: 11px; font-weight: 700;
      letter-spacing: 0.12em; color: rgba(255,255,255,0.65); text-align: center;
      text-transform: uppercase; margin-bottom: 32px;
    }
    /* VAULT DOOR */
    .lg-vault-door {
      width: 140px; height: 160px; margin: 0 auto 40px;
      position: relative; perspective: 500px;
      overflow: visible;
    }
    /* vault frame / back wall */
    .lg-vault-frame {
      width: 100%; height: 100%;
      background: linear-gradient(145deg, #0d0820, #060410);
      border-radius: 12px;
      border: 2px solid rgba(139,92,246,0.5);
      box-shadow: 0 0 40px rgba(139,92,246,0.12);
      position: absolute; inset: 0;
      display: flex; align-items: center; justify-content: center;
    }
    .lg-vault-interior {
      display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px;
      opacity: 0; transition: opacity 0.5s ease 0.6s;
    }
    .lg-vault-interior.visible { opacity: 1; }
    .lg-vault-interior-icon { font-size: 24px; }
    .lg-vault-interior-text {
      font-size: 10px; font-weight: 700; letter-spacing: 0.1em;
      color: var(--green); text-transform: uppercase; font-family: 'Plus Jakarta Sans', sans-serif;
    }

    /* the door panel — hinged on left */
    .lg-vault-door-panel {
      width: 100%; height: 100%;
      position: absolute; inset: 0;
      transform-origin: left center;
      transform-style: preserve-3d;
      transition: transform 1.1s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .lg-vault-door-panel.open {
      transform: perspective(500px) rotateY(-82deg);
    }
    /* front face — steel */
    .lg-vault-door-front {
      position: absolute; inset: 0;
      background: linear-gradient(145deg, #8E9EAB, #6B7A85, #4A5568, #6B7A85, #8E9EAB);
      border-radius: 12px;
      border: 2px solid #A0ADB8;
      box-shadow: 0 4px 24px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -1px 0 rgba(0,0,0,0.3);
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      gap: 10px; backface-visibility: visible;
    }
    .lg-vault-door-front::before {
      content: ''; position: absolute; inset: 6px;
      border: 1px solid rgba(255,255,255,0.15); border-radius: 8px;
      background: linear-gradient(145deg, rgba(255,255,255,0.05), transparent);
    }
    /* hinge decorations */
    .lg-vault-hinges {
      position: absolute; left: 8px; top: 0; bottom: 0;
      display: flex; flex-direction: column; justify-content: space-evenly; pointer-events: none;
    }
    .lg-vault-hinge {
      width: 8px; height: 20px;
      background: linear-gradient(180deg, #C8D0D8, #8E9EAB, #6B7A85);
      border-radius: 3px;
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.4), 0 2px 4px rgba(0,0,0,0.4);
    }

    .lg-vault-lock {
      width: 40px; height: 40px;
      background: linear-gradient(135deg, #C8D0D8, #8E9EAB);
      border-radius: 50%; display: flex; align-items: center; justify-content: center;
      font-size: 18px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.4);
      transition: background 0.6s ease, box-shadow 0.6s ease;
      position: relative; z-index: 1;
    }
    .lg-vault-lock.unlocked {
      background: linear-gradient(135deg, #22C55E, #34D399);
      box-shadow: 0 0 30px rgba(34,197,94,0.6), inset 0 1px 0 rgba(255,255,255,0.3);
      animation: none;
    }
    .lg-vault-lock.locked { animation: lockPulse 3s ease-in-out infinite; }
    @keyframes lockPulse {
      0%, 100% { box-shadow: 0 2px 8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.4); }
      50% { box-shadow: 0 2px 8px rgba(0,0,0,0.5), 0 0 20px rgba(139,92,246,0.4), inset 0 1px 0 rgba(255,255,255,0.4); }
    }
    .lg-vault-slots { display: flex; gap: 6px; }
    .lg-vault-slot {
      width: 10px; height: 14px; border-radius: 5px;
      border: 2px solid rgba(0,0,0,0.3);
      background: rgba(0,0,0,0.2);
      transition: all 0.5s ease;
    }
    .lg-vault-slot.filled {
      background: var(--grad-pb); border-color: transparent;
      box-shadow: 0 0 10px rgba(139,92,246,0.6);
    }

    /* KEYHOLDERS ROW */
    .lg-keyholders {
      display: flex; gap: 12px; justify-content: center; flex-wrap: wrap;
    }
    .lg-kh {
      display: flex; flex-direction: column; align-items: center; gap: 8px;
      flex: 1; min-width: 80px; max-width: 120px;
    }
    .lg-kh-avatar {
      width: 52px; height: 52px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-size: 22px;
      border: 2px solid rgba(255,255,255,0.15);
      background: rgba(255,255,255,0.08);
      transition: all 0.5s ease;
      position: relative;
    }
    .lg-kh-avatar.approved {
      border-color: var(--purple);
      background: rgba(139,92,246,0.25);
      box-shadow: 0 0 20px rgba(139,92,246,0.4);
    }
    .lg-kh-avatar.approved::after {
      content: '✓'; position: absolute; bottom: -2px; right: -2px;
      width: 18px; height: 18px; background: var(--green);
      border-radius: 50%; font-size: 10px; font-weight: 700; color: #fff;
      display: flex; align-items: center; justify-content: center;
      border: 2px solid #162030;
    }
    .lg-kh-name {
      font-size: 12px; font-weight: 700; color: rgba(255,255,255,0.85);
      text-align: center; font-family: 'Plus Jakarta Sans', sans-serif;
      letter-spacing: 0.02em;
    }
    .lg-kh-role {
      font-size: 11px; color: rgba(255,255,255,0.45);
      text-align: center; font-family: 'Plus Jakarta Sans', sans-serif;
    }
    .lg-kh-key {
      font-size: 16px;
      transition: all 0.5s ease;
      filter: grayscale(1) opacity(0.4);
    }
    .lg-kh-avatar.approved .lg-kh-key { filter: grayscale(0) opacity(1); }

    .lg-vault-status {
      text-align: center; margin-top: 24px;
      font-size: 12px; font-weight: 700; letter-spacing: 0.06em;
      color: rgba(255,255,255,0.6); font-family: 'Plus Jakarta Sans', sans-serif;
      text-transform: uppercase;
    }
    .lg-vault-status span { color: var(--purple-l); }
    .lg-vault-status.unlocked span { color: var(--green); }

    /* WAVE */
    .lg-wave { display: block; width: 100%; overflow: hidden; line-height: 0; }
    .lg-wave svg { display: block; width: 100%; }

    /* DARK SECTION — now light */
    .lg-dark { background: #FAF7F2; padding: 0; }
    .lg-section { padding: 48px 48px 40px; max-width: 1100px; margin: 0 auto; }

    /* HOW IT WORKS */
    .lg-hiw-label {
      font-size: 11px; font-weight: 700; letter-spacing: 0.14em;
      text-transform: uppercase; color: var(--purple); margin-bottom: 16px;
    }
    .lg-hiw-h2 {
      font-family: 'Fraunces', serif; font-weight: 900;
      font-size: clamp(36px, 4vw, 56px); color: var(--plum);
      line-height: 1.1; margin-bottom: 20px;
    }
    .lg-hiw-h2 em { font-style: italic; color: var(--purple); }
    .lg-hiw-sub { font-size: 18px; color: #6B7280; max-width: 520px; line-height: 1.6; margin-bottom: 64px; }

    .lg-steps { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 24px; }
    .lg-step {
      background: #fff; border-radius: 20px; padding: 32px;
      border: 1px solid #EDE9E3;
      box-shadow: 0 2px 16px rgba(30,16,64,0.06);
      transition: transform 0.3s, border-color 0.3s, box-shadow 0.3s;
    }
    .lg-step:hover { transform: translateY(-4px); border-color: rgba(139,92,246,0.3); box-shadow: 0 8px 32px rgba(139,92,246,0.1); }
    .lg-step-num {
      width: 40px; height: 40px; border-radius: 12px;
      background: var(--grad-pb); display: flex; align-items: center; justify-content: center;
      font-weight: 800; font-size: 16px; color: #fff; margin-bottom: 20px;
    }
    .lg-step h3 {
      font-family: 'Fraunces', serif; font-weight: 700; font-size: 20px;
      color: var(--plum); margin-bottom: 10px;
    }
    .lg-step p { font-size: 14px; color: #6B7280; line-height: 1.6; }

    /* KEYHOLDER EXPLAINER */
    .lg-khe { padding: 20px 48px 40px; max-width: 1100px; margin: 0 auto; }
    .lg-khe-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: center; }
    @media(max-width:768px){ .lg-khe-grid { grid-template-columns: 1fr; gap: 48px; } }

    .lg-khe-card {
      background: #fff; border-radius: 24px; padding: 40px;
      border: 1px solid #EDE9E3;
      box-shadow: 0 4px 24px rgba(30,16,64,0.07);
    }
    .lg-khe-rule {
      background: rgba(139,92,246,0.06); border: 1px solid rgba(139,92,246,0.15);
      border-radius: 12px; padding: 16px 20px; margin-bottom: 24px;
      display: flex; align-items: center; gap: 12px;
    }
    .lg-khe-rule-icon { font-size: 20px; }
    .lg-khe-rule-text { font-size: 13px; font-weight: 600; color: var(--purple); }

    .lg-khe-people { display: flex; flex-direction: column; gap: 12px; }
    .lg-khe-person {
      display: flex; align-items: center; gap: 14px;
      background: #FAF7F2; border-radius: 12px; padding: 14px 16px;
      border: 1px solid #EDE9E3;
      transition: border-color 0.3s;
    }
    .lg-khe-person.approved { border-color: rgba(34,197,94,0.3); background: rgba(34,197,94,0.04); }
    .lg-khe-person.pending { border-color: rgba(251,146,60,0.2); }
    .lg-khe-person-avatar { font-size: 24px; }
    .lg-khe-person-info { flex: 1; }
    .lg-khe-person-name { font-size: 14px; font-weight: 600; color: var(--plum); }
    .lg-khe-person-role { font-size: 12px; color: #9CA3AF; margin-top: 2px; }
    .lg-khe-badge {
      font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 100px;
    }
    .lg-khe-badge.approved { background: rgba(34,197,94,0.12); color: #16A34A; }
    .lg-khe-badge.pending { background: rgba(251,146,60,0.12); color: #D97706; }

    .lg-khe-text .lg-hiw-label { margin-bottom: 12px; }
    .lg-khe-text h2 {
      font-family: 'Fraunces', serif; font-weight: 900;
      font-size: clamp(32px, 3.5vw, 48px); color: var(--plum); line-height: 1.1; margin-bottom: 20px;
    }
    .lg-khe-text h2 em { font-style: italic; color: var(--purple); }
    .lg-khe-text p { font-size: 16px; color: #6B7280; line-height: 1.7; margin-bottom: 16px; }

    /* WHAT'S INSIDE */
    .lg-inside { padding: 0 48px 40px; max-width: 1100px; margin: 0 auto; }
    .lg-inside-h2 {
      font-family: 'Fraunces', serif; font-weight: 900;
      font-size: clamp(32px, 4vw, 52px); color: var(--plum);
      text-align: center; margin-bottom: 16px; line-height: 1.1;
    }
    .lg-inside-h2 em { font-style: italic; color: var(--purple); }
    .lg-inside-sub { text-align: center; color: #6B7280; font-size: 17px; margin-bottom: 56px; }
    .lg-inside-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; }
    .lg-inside-item {
      background: #fff; border-radius: 16px; padding: 24px;
      border: 1px solid #EDE9E3;
      box-shadow: 0 2px 12px rgba(30,16,64,0.05);
      transition: transform 0.3s, border-color 0.3s;
    }
    .lg-inside-item:hover { transform: translateY(-3px); border-color: rgba(139,92,246,0.25); }
    .lg-inside-icon { font-size: 28px; margin-bottom: 12px; }
    .lg-inside-item h4 { font-size: 15px; font-weight: 700; color: var(--plum); margin-bottom: 6px; }
    .lg-inside-item p { font-size: 13px; color: #9CA3AF; line-height: 1.5; }

    /* NOT SECTION */
    .lg-not { padding: 0 48px 80px; max-width: 1100px; margin: 0 auto; }
    .lg-not-inner {
      background: linear-gradient(135deg, rgba(139,92,246,0.05), rgba(56,189,248,0.03));
      border: 1px solid rgba(139,92,246,0.12); border-radius: 24px; padding: 56px;
      display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: start;
    }
    @media(max-width:768px){ .lg-not-inner { grid-template-columns: 1fr; padding: 32px; } }
    .lg-not h2 {
      font-family: 'Fraunces', serif; font-weight: 900;
      font-size: clamp(28px, 3vw, 42px); color: var(--plum); margin-bottom: 12px; line-height: 1.1;
    }
    .lg-not h2 em { font-style: italic; color: var(--purple); }
    .lg-not-sub { color: #6B7280; font-size: 16px; line-height: 1.6; }
    .lg-not-list { list-style: none; display: flex; flex-direction: column; gap: 14px; }
    .lg-not-list li {
      display: flex; align-items: flex-start; gap: 12px;
      font-size: 15px; color: #374151; line-height: 1.5;
    }
    .lg-not-list li::before { content: '✓'; color: var(--purple); font-weight: 800; flex-shrink: 0; margin-top: 1px; }
    .lg-not-list li.no::before { content: '✗'; color: #D1D5DB; }
    .lg-not-list li.no { color: #9CA3AF; text-decoration: line-through; }

    /* PRICING */
    .lg-pricing { padding: 0 48px 80px; max-width: 800px; margin: 0 auto; text-align: center; }
    .lg-pricing h2 {
      font-family: 'Fraunces', serif; font-weight: 900;
      font-size: clamp(32px, 4vw, 52px); color: var(--plum); margin-bottom: 12px;
    }
    .lg-pricing h2 em { font-style: italic; color: var(--purple); }
    .lg-pricing-sub { color: #6B7280; font-size: 17px; margin-bottom: 48px; }
    .lg-price-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
    @media(max-width:600px){ .lg-price-cards { grid-template-columns: 1fr; } }
    .lg-price-card {
      background: #fff; border-radius: 20px; padding: 36px;
      border: 1px solid #EDE9E3; text-align: left;
      box-shadow: 0 2px 16px rgba(30,16,64,0.06);
      transition: transform 0.3s;
    }
    .lg-price-card:hover { transform: translateY(-4px); }
    .lg-price-card.featured {
      border-color: rgba(139,92,246,0.35);
      background: linear-gradient(145deg, rgba(139,92,246,0.04), #fff);
      box-shadow: 0 8px 40px rgba(139,92,246,0.12);
    }
    .lg-price-badge {
      display: inline-block; background: var(--grad-pb);
      color: #fff; font-size: 11px; font-weight: 700; letter-spacing: 0.06em;
      padding: 4px 12px; border-radius: 100px; margin-bottom: 20px; text-transform: uppercase;
    }
    .lg-price-amount { font-family: 'Fraunces', serif; font-weight: 900; font-size: 48px; color: var(--plum); }
    .lg-price-amount span { font-size: 20px; font-weight: 400; color: #9CA3AF; }
    .lg-price-desc { font-size: 13px; color: #9CA3AF; margin: 8px 0 24px; }
    .lg-price-features { list-style: none; display: flex; flex-direction: column; gap: 10px; }
    .lg-price-features li {
      display: flex; align-items: center; gap: 10px;
      font-size: 14px; color: #374151;
    }
    .lg-price-features li::before { content: '✓'; color: var(--purple); font-weight: 800; }

    /* CTA LIGHT */
    .lg-cta-light { background: var(--cream); padding: 100px 48px; text-align: center; }
    .lg-cta-light h2 {
      font-family: 'Fraunces', serif; font-weight: 900;
      font-size: clamp(36px, 5vw, 64px); color: var(--plum); margin-bottom: 16px; line-height: 1.1;
    }
    .lg-cta-light h2 em { font-style: italic; color: var(--purple); }
    .lg-cta-light p { font-size: 18px; color: #6B7280; margin-bottom: 40px; }
    .lg-cta-trial { font-size: 13px; color: #9CA3AF; margin-top: 16px; }

    /* FOOTER */
    .lg-foot {
      background: var(--cream-d); border-top: 1px solid rgba(30,16,64,0.08);
      padding: 40px 48px; display: flex; align-items: center; justify-content: space-between;
      flex-wrap: wrap; gap: 16px;
    }
    .lg-foot-logo {
      font-family: 'Fraunces', serif; font-weight: 900; font-size: 20px;
      color: var(--plum); border: none; background: none; cursor: pointer;
    }
    .lg-foot-logo span { color: var(--purple); font-style: italic; }
    .lg-foot-links { display: flex; gap: 24px; }
    .lg-foot-links a { font-size: 13px; color: #9CA3AF; text-decoration: none; transition: color 0.2s; }
    .lg-foot-links a:hover { color: var(--purple); }
    .lg-foot-copy { font-size: 12px; color: #9CA3AF; }

    /* REVEAL ANIMATIONS */
    .reveal { opacity: 0; transform: translateY(32px); transition: opacity 0.7s ease, transform 0.7s ease; }
    .reveal.revealed { opacity: 1; transform: none; }
    .reveal-left { opacity: 0; transform: translateX(-32px); transition: opacity 0.7s ease, transform 0.7s ease; }
    .reveal-left.revealed { opacity: 1; transform: none; }
    .reveal-right { opacity: 0; transform: translateX(32px); transition: opacity 0.7s ease, transform 0.7s ease; }
    .reveal-right.revealed { opacity: 1; transform: none; }

    /* TICKER */
    .lg-ticker { background: #F0EBE1; padding: 16px 0; overflow: hidden; border-top: 1px solid rgba(30,16,64,0.06); border-bottom: 1px solid rgba(30,16,64,0.06); }
    .lg-ticker-track { display: flex; gap: 0; animation: ticker 28s linear infinite; white-space: nowrap; }
    .lg-ticker-track:hover { animation-play-state: paused; }
    .lg-ticker-item { display: inline-flex; align-items: center; gap: 10px; padding: 0 40px; font-size: 13px; font-weight: 600; color: #9CA3AF; letter-spacing: 0.04em; }
    .lg-ticker-dot { width: 4px; height: 4px; border-radius: 50%; background: var(--purple); }
    @keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }

    @media(max-width:768px){
      .lg-nav { padding: 16px 24px; }
      .lg-hero { padding: 100px 24px 60px; }
      .lg-section, .lg-khe, .lg-inside, .lg-not, .lg-pricing { padding-left: 24px; padding-right: 24px; }
      .lg-foot { padding: 32px 24px; }
      .lg-cta-light { padding: 80px 24px; }
    }
  `;


  const tickerItems = [
    '🛡️ Vault Guardian System', '🔐 Privacy First', '📋 Estate Organized',
    '👨‍👩‍👧‍👦 Built for Families', '🚫 No Bank Connections', '🔒 End-to-End Private',
    '📄 Documents & Wishes', '💜 Peace of Mind', '🛡️ Vault Guardian System',
    '🔐 Privacy First', '📋 Estate Organized', '👨‍👩‍👧‍👦 Built for Families',
    '🚫 No Bank Connections', '🔒 End-to-End Private', '📄 Documents & Wishes', '💜 Peace of Mind',
  ];

  return (
    <>
      <style>{style}</style>

      {/* NAV */}
      <nav className={`lg-nav${scrolled ? ' dark' : ''}`}>
        <button className="lg-nav-logo">Merq<span>Legacy</span></button>
        <button className="lg-nav-cta" onClick={goToAuth}>Get Started — $69/yr</button>
      </nav>

      {/* HERO */}
      <section className="lg-hero" style={{paddingBottom: 0}}>
        <div className="lg-hero-bg" />
        <div className="lg-hero-eyebrow">30-Day Money-Back Guarantee — No Questions Asked</div>
        <h1 className="lg-hero-h1">
          Everything your family<br />needs to find, <em>organized.</em>
        </h1>
        <p className="lg-hero-sub" style={{marginBottom: 0}}>
          Merq Legacy is the secure place where families store everything that matters — so when it's needed most, it's already there.
        </p>
      </section>

      {/* INTERACTIVE DEMO VAULT — directly under hero */}
      <DemoVault />

      {/* TICKER */}
      <div className="lg-ticker">
        <div className="lg-ticker-track">
          {tickerItems.map((item, i) => (
            <span key={i} className="lg-ticker-item">
              <span className="lg-ticker-dot" />{item}
            </span>
          ))}
        </div>
      </div>

      {/* HOW IT WORKS */}
      <div className="lg-dark">
        <div className="lg-section">
          <div className="lg-hiw-label">How It Works</div>
          <h2 className="lg-hiw-h2">Simple to set up.<br /><em>Priceless</em> when needed.</h2>
          <p className="lg-hiw-sub">Takes about 30 minutes to organize everything. Takes your family seconds to access it when it matters.</p>
          <div className="lg-steps">
            {[
              { n: '1', title: 'Build your vault', desc: 'Add accounts, documents, contacts, assets, and final wishes — everything in one organized place.' },
              { n: '2', title: 'Designate Vault Guardians', desc: 'Choose 3 or 4 trusted people. At least 3 must approve every access request — no one can ever open your vault alone.' },
              { n: '3', title: 'Rest easy', desc: 'Your vault is encrypted and private. Nothing is accessible until your keyholders approve it together.' },
              { n: '4', title: 'Family gets access', desc: 'When needed, a Vault Guardian requests access. Others approve. Vault opens. No lawyers, no death certificates, no delays.' },
            ].map((s, i) => (
              <div className="lg-step reveal" key={i} style={{transitionDelay: `${i * 0.1}s`}}>
                <div className="lg-step-num">{s.n}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* KEYHOLDER EXPLAINER */}
        <div className="lg-khe">
          <div className="lg-khe-grid">
            <div className="lg-khe-card">
              <div className="lg-khe-rule">
                <span className="lg-khe-rule-icon">🛡️</span>
                <span className="lg-khe-rule-text">Vault Guardian rule: 3 of 3 must approve</span>
              </div>
              <div className="lg-khe-people">
                {[
                  { emoji: '👩', name: 'Mom', role: 'Spouse', status: 'approved' },
                  { emoji: '👨‍💼', name: 'James', role: 'Estate Attorney', status: 'approved' },
                  { emoji: '👩‍💼', name: 'Sarah', role: 'Daughter', status: 'pending' },
                ].map((p, i) => (
                  <div key={i} className={`lg-khe-person ${p.status}`}>
                    <span className="lg-khe-person-avatar">{p.emoji}</span>
                    <div className="lg-khe-person-info">
                      <div className="lg-khe-person-name">{p.name}</div>
                      <div className="lg-khe-person-role">{p.role}</div>
                    </div>
                    <span className={`lg-khe-badge ${p.status}`}>{p.status === 'approved' ? 'Approved ✓' : 'Pending...'}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg-khe-text">
              <div className="lg-hiw-label">The Vault Guardian System</div>
              <h2>The only vault that <em>requires trust</em> to open.</h2>
              <p>No other estate organizer has this. Instead of a password or code that anyone can steal, your vault requires real people — people you choose — to approve access together.</p>
              <p>It mirrors how real trusts and estates work in the legal world, without the attorneys and paperwork. The key isn't a string of characters. The key is the people you trust most.</p>
            </div>
          </div>
        </div>

        {/* WHAT'S INSIDE */}
        <div className="lg-inside">
          <h2 className="lg-inside-h2">Everything that <em>matters</em> — in one place.</h2>
          <p className="lg-inside-sub">Stop leaving your family to piece it together.</p>
          <div className="lg-inside-grid">
            {[
              { icon: '🏦', title: 'Financial Accounts', desc: 'Bank accounts, investments, retirement — with institution and contact info.' },
              { icon: '📋', title: 'Key Contacts', desc: 'Attorney, accountant, financial advisor, doctor — all in one place.' },
              { icon: '🏠', title: 'Assets', desc: 'Real estate, vehicles, valuables, and where to find the titles.' },
              { icon: '📄', title: 'Documents', desc: 'Upload wills, trusts, deeds, passports, insurance policies.' },
              { icon: '💻', title: 'Digital Life', desc: 'Online accounts and subscriptions your family will need to manage.' },
              { icon: '🔒', title: 'Insurance', desc: 'Life, health, home, auto — policy numbers and contact info.' },
              { icon: '💌', title: 'Final Wishes', desc: 'Funeral preferences, organ donation, letters to the people you love.' },
              { icon: '🛡️', title: 'Vault Guardians', desc: 'Designate 3 or 4 trusted people. All 3 minimum must agree before access is ever granted.' },
            ].map((item, i) => (
              <div key={i} className="lg-inside-item reveal" style={{transitionDelay: `${i * 0.07}s`}}>
                <div className="lg-inside-icon">{item.icon}</div>
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* WHAT IT'S NOT */}
        <div className="lg-not">
          <div className="lg-not-inner">
            <div>
              <h2>What Merq Legacy <em>is not.</em></h2>
              <p className="lg-not-sub">We're not trying to replace your attorney. We're the thing your attorney wishes you had before you walked in the door.</p>
            </div>
            <ul className="lg-not-list">
              <li>A secure, organized place for your family to find everything</li>
              <li>Private — your data is never sold or shared</li>
              <li>Built for real families, not attorneys</li>
              <li>Priced for everyone — not $500/year</li>
              <li className="no">A will or legal document generator</li>
              <li className="no">A law firm or legal service</li>
              <li className="no">Connected to your bank accounts</li>
            </ul>
          </div>
        </div>

        {/* PRICING */}
        <div className="lg-pricing">
          <h2>Simple, <em>honest</em> pricing.</h2>
          <p className="lg-pricing-sub">We make money when you subscribe. That's it. No ads, no data selling, no surprises.</p>
          <div style={{maxWidth: 400, margin: '0 auto'}}>
            <div className="lg-price-card featured">
              <div className="lg-price-badge">30-Day Money-Back Guarantee</div>
              <div className="lg-price-amount">$69<span>/yr</span></div>
              <p className="lg-price-desc">That's $5.75/month — less than a coffee. Cancel anytime.</p>
              <ul className="lg-price-features">
                <li>Full vault access</li>
                <li>Up to 5 Vault Guardians (minimum 3)</li>
                <li>Unlimited document storage</li>
                <li>Family sharing</li>
                <li>Priority support</li>
                <li>30-day money-back guarantee</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* WAVE UP */}
      <div className="lg-wave">
        <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{height:80}}>
          <path d="M0,80 C360,0 1080,80 1440,0 L1440,80 L0,80 Z" fill="#0B1220" />
        </svg>
      </div>

      {/* CTA */}
      <section className="lg-cta-light">
        <h2>Give your family the gift<br />of <em>clarity.</em></h2>
        <p>Join thousands of families who've organized their legacy. If you're not satisfied within 30 days, we'll refund you — no questions asked.</p>
        <button className="lg-btn-primary" onClick={goToAuth} style={{fontSize: 18, padding: '18px 48px'}}>Get Started — $69/year</button>
        <p className="lg-cta-trial">30-day money-back guarantee · Cancel anytime · No hidden fees</p>
      </section>

      {/* FOOTER */}
      <footer className="lg-foot">
        <button className="lg-foot-logo">Merq<span>Legacy</span></button>
        <div className="lg-foot-links">
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="/refund">Refund Policy</a>
          <a href="mailto:hello@getmerq.com">Contact</a>
        </div>
        <span className="lg-foot-copy">© 2026 Merq Software</span>
      </footer>
    </>
  );
}
